/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from "react";
import {
  accessAPI,
  formatMonto,
  formatFecha,
  Titulo,
  Boton,
  Formulario,
  Sidebar,
  tituloBase,
  TablaFiltrable,
  usePermiso
} from "../../shared";

import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import AjustePropuestas from "./AjustePropuestas";

// Opciones/labels de frecuencia de refacturación (coinciden con el enum del API).
const FRECUENCIA_OPCIONES = [
  { value: "NINGUNA", etiqueta: "Ninguna (facturación única)" },
  { value: "SEMANAL", etiqueta: "Semanal" },
  { value: "MENSUAL", etiqueta: "Mensual" },
  { value: "SEMESTRAL", etiqueta: "Semestral" },
  { value: "ANUAL", etiqueta: "Anual" },
  { value: "BIENAL", etiqueta: "Bienal" },
  { value: "TRIENAL", etiqueta: "Trienal" }
];
const frecuenciaLabel = (valor) =>
  FRECUENCIA_OPCIONES.find((f) => f.value === valor)?.etiqueta || "—";

export default function ListaPrecios() {
  // Ajustar propuestas modifica propuestas comerciales: pide full en facturación
  const { puedeEditar: puedeAjustarPropuestas } = usePermiso("admin.facturacion");
  const [precios, setPrecios] = useState();
  const [loading, setLoading] = useState(true);

  // Filtros (estilo actividades abiertas)
  const [busqueda, setBusqueda] = useState("");
  const [filtroActividad, setFiltroActividad] = useState([]);
  const [filtroCertificado, setFiltroCertificado] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  const toast = useRef(null);

  // Sidebar de edición individual
  const [editVisible, setEditVisible] = useState(false);
  const [editLoader, setEditLoader] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEditKey, setFormEditKey] = useState(0);

  // Sidebar de modificación masiva (bulk)
  const [bulkVisible, setBulkVisible] = useState(false);
  const [ajusteVisible, setAjusteVisible] = useState(false);
  const [bulkLoader, setBulkLoader] = useState(false);
  const [porcentaje, setPorcentaje] = useState(0);
  const [seleccion, setSeleccion] = useState([]);
  const [nuevosPrecios, setNuevosPrecios] = useState({});
  const [bulkExpanded, setBulkExpanded] = useState({});
  // Filtros dentro del bulk (para acotar a actividades/certificados puntuales)
  const [bulkBusqueda, setBulkBusqueda] = useState("");
  const [bulkFiltroActividad, setBulkFiltroActividad] = useState([]);
  const [bulkFiltroCertificado, setBulkFiltroCertificado] = useState([]);

  useEffect(() => {
    document.title = "Lista de precios";
    accessAPI(
      "GET",
      "admin/actividad/precios",
      null,
      (respuesta) => {
        setPrecios(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar la lista de precios",
          sticky: true
        });
        setLoading(false);
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  // ---------- Helpers de presentación ----------
  const certificadoNombre = (fila) =>
    `${fila.certificadotipo?.procesotipo?.nombre} - ${fila.certificadotipo?.nombre}`;

  const precioBody = (fila) =>
    fila.precio != null
      ? `${fila.moneda?.simbolo || ""} ${formatMonto(fila.precio)}`
      : "—";

  const monedaBody = (fila) => fila.moneda?.nombre || "—";

  const actualizacionBody = (fila) =>
    fila.fechaActualizacionPrecio
      ? formatFecha(fila.fechaActualizacionPrecio)
      : "—";

  const accionBody = (fila) => (
    <Boton icono texto callback={() => abrirEditar(fila)}>
      <i className="pi pi-pencil" />
    </Boton>
  );

  // ---------- Filtrado + agrupado por tipo de certificado ----------
  const opcionesDesde = (valores) =>
    [...new Set(valores)]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ label: v, value: v }));

  const actividadOptions = useMemo(
    () => opcionesDesde((precios || []).map((p) => p.actividadtipo?.nombre)),
    [precios]
  );
  const certificadoOptions = useMemo(
    () => opcionesDesde((precios || []).map((p) => certificadoNombre(p))),
    [precios]
  );

  const hayFiltro =
    busqueda.trim() !== "" ||
    filtroActividad.length > 0 ||
    filtroCertificado.length > 0;

  // Aplica búsqueda + MultiSelect sobre el detalle (filas planas)
  const preciosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return (precios || []).filter((p) => {
      const certNombre = certificadoNombre(p);
      if (texto) {
        const enFila = [
          p.actividadtipo?.nombre,
          certNombre,
          p.moneda?.nombre
        ]
          .join(" ")
          .toLowerCase();
        if (!enFila.includes(texto)) return false;
      }
      if (
        filtroActividad.length &&
        !filtroActividad.includes(p.actividadtipo?.nombre)
      )
        return false;
      if (filtroCertificado.length && !filtroCertificado.includes(certNombre))
        return false;
      return true;
    });
  }, [precios, busqueda, filtroActividad, filtroCertificado]);

  // Agrupa el detalle filtrado por tipo de certificado
  const grupos = useMemo(() => {
    const mapa = new Map();
    preciosFiltrados.forEach((p) => {
      const key = p.certificadotipoid;
      if (!mapa.has(key)) {
        mapa.set(key, {
          id: key,
          certNombre: certificadoNombre(p),
          actividades: []
        });
      }
      mapa.get(key).actividades.push(p);
    });
    return [...mapa.values()].sort((a, b) =>
      a.certNombre.localeCompare(b.certNombre)
    );
  }, [preciosFiltrados]);

  // Con filtro activo, auto-expande los grupos con coincidencias
  useEffect(() => {
    if (hayFiltro) {
      const exp = {};
      grupos.forEach((g) => {
        exp[g.id] = true;
      });
      setExpandedRows(exp);
    } else {
      setExpandedRows({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, filtroActividad, filtroCertificado]);

  // Tabla interna que se muestra al expandir un tipo de certificado
  const expansionTemplate = (grupo) => (
    <div css={{ padding: "0.5rem 1rem" }}>
      <TablaFiltrable conFiltros={false}
        value={grupo.actividades}
        size="small"
        nombreArchivo="precios_actividades"
      >
        <Column header="Actividad" field="actividadtipo.nombre" />
        <Column header="Precio" body={precioBody} style={{ width: "10em" }} />
        <Column header="Moneda" body={monedaBody} style={{ width: "9em" }} />
        <Column
          header="Refacturación"
          body={(fila) => frecuenciaLabel(fila.frecuenciaRefacturacion)}
          style={{ width: "11em" }}
        />
        <Column
          header="Última actualización"
          body={actualizacionBody}
          style={{ width: "12em" }}
        />
        <Column
          body={accionBody}
          style={{ width: "3rem", textAlign: "center" }}
        />
      </TablaFiltrable>
    </div>
  );

  // ---------- Edición individual ----------
  function abrirEditar(fila) {
    setEditando(fila);
    setFormEditKey((prev) => prev + 1);
    setEditVisible(true);
  }

  function getEditFormConfig() {
    return {
      secciones: [
        {
          // El título lo renderiza el Formulario (liviano), no un <Titulo> pesado
          nombre: "Editar precio",
          descripcion: editando
            ? `${editando.actividadtipo?.nombre} · ${certificadoNombre(editando)}`
            : "",
          campos: [
            {
              nombre: "crearautomaticamente",
              tipoitemformularioid: 6,
              etiqueta:
                "Crear automáticamente junto con un proceso de este tipo",
              respuesta: editando?.crearautomaticamente
            },
            {
              nombre: "precio",
              tipoitemformularioid: 14,
              etiqueta: "Precio",
              respuesta: {
                precio: editando?.precio ?? null,
                monedaId: editando?.moneda?.id ?? null
              }
            },
            {
              nombre: "frecuenciaRefacturacion",
              tipoitemformularioid: 5,
              etiqueta: "Frecuencia de refacturación",
              opciones: FRECUENCIA_OPCIONES,
              respuesta: editando?.frecuenciaRefacturacion || "NINGUNA"
            }
          ]
        }
      ],
      botones: [{ etiqueta: "Guardar", callback: guardarEdicion, validar: true }]
    };
  }

  function guardarEdicion(camposFormulario) {
    // El campo de precio (tipo 14) emite { precio, monedaId }; se aplana a las
    // claves precio/monedaid que espera el backend.
    const { precio, ...resto } = camposFormulario;
    const payload = {
      ...resto,
      precio: precio?.precio ?? null,
      monedaid: precio?.monedaId ?? null
    };
    setEditLoader(true);
    accessAPI(
      "PUT",
      `admin/actividad/asignaracertificadotipo/${editando.id}`,
      payload,
      (respuesta) => {
        setPrecios((prev) =>
          prev.map((p) =>
            p.id === editando.id
              ? {
                  ...p,
                  ...respuesta,
                  fechaActualizacionPrecio: new Date().toISOString()
                }
              : p
          )
        );
        setEditLoader(false);
        setEditVisible(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Precio actualizado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setEditLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar",
          sticky: true
        });
      }
    );
  }

  // ---------- Modificación masiva (bulk) ----------
  // Solo asignaciones que ya tienen precio (el % se aplica sobre el actual)
  function abrirBulk() {
    const conPrecio = (precios || []).filter((p) => p.precio != null);
    setPorcentaje(0);
    setBulkBusqueda("");
    setBulkFiltroActividad([]);
    setBulkFiltroCertificado([]);
    setSeleccion(conPrecio);
    setNuevosPrecios(
      Object.fromEntries(conPrecio.map((p) => [p.id, p.precio]))
    );
    // Expande todos los tipos de certificado por defecto
    const exp = {};
    [...new Set(conPrecio.map((p) => p.certificadotipoid))].forEach((k) => {
      exp[k] = true;
    });
    setBulkExpanded(exp);
    setBulkVisible(true);
  }

  // Mezcla la selección de un grupo (tipo de certificado) con la selección global
  function mergeGroupSelection(grupo, nuevos) {
    setSeleccion((prev) => {
      const ids = new Set(grupo.actividades.map((a) => a.id));
      return [...prev.filter((s) => !ids.has(s.id)), ...nuevos];
    });
  }

  function recalcularPrecios() {
    setNuevosPrecios((prev) => {
      const actualizado = { ...prev };
      // Solo recalcula las filas visibles (según los filtros del bulk)
      bulkVisibleRows.forEach((p) => {
        actualizado[p.id] = Math.round(
          p.precio * (1 + (porcentaje || 0) / 100)
        );
      });
      return actualizado;
    });
  }

  function aplicarBulk() {
    // Solo los seleccionados al momento del click cuyo precio nuevo difiere del actual
    const cambios = cambiosEfectivos.map((s) => ({
      id: s.id,
      precio: nuevosPrecios[s.id]
    }));

    if (!cambios.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Sin cambios",
        detail: "No hay precios modificados en las filas seleccionadas",
        life: 3000
      });
      return;
    }

    setBulkLoader(true);
    accessAPI(
      "PUT",
      "admin/actividad/precios/bulk",
      { cambios },
      () => {
        const ahora = new Date().toISOString();
        const mapaCambios = Object.fromEntries(
          cambios.map((c) => [c.id, c.precio])
        );
        setPrecios((prev) =>
          prev.map((p) =>
            mapaCambios[p.id] != null
              ? {
                  ...p,
                  precio: mapaCambios[p.id],
                  fechaActualizacionPrecio: ahora
                }
              : p
          )
        );
        setBulkLoader(false);
        setBulkVisible(false);
        toast.current?.show({
          severity: "success",
          summary: "Precios actualizados",
          detail: `${cambios.length} precio(s) actualizado(s)`,
          life: 3000
        });
      },
      (respuesta) => {
        setBulkLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar precios",
          sticky: true
        });
      }
    );
  }

  const bulkPrecioActualBody = (fila) =>
    `${fila.moneda?.simbolo || ""} ${formatMonto(fila.precio)}`;

  const bulkPrecioNuevoBody = (fila) => (
    <InputNumber
      value={fila._nuevo}
      onValueChange={(e) =>
        setNuevosPrecios((prev) => ({ ...prev, [fila.id]: e.value }))
      }
      mode="decimal"
      useGrouping
      prefix={fila.moneda?.simbolo ? `${fila.moneda.simbolo} ` : ""}
      inputStyle={{ width: "7rem" }}
    />
  );

  // Filas del bulk: solo con precio y que pasen los filtros del bulk
  const bulkVisibleRows = (precios || [])
    .filter((p) => p.precio != null)
    .filter((p) => {
      const certNombre = certificadoNombre(p);
      const texto = bulkBusqueda.trim().toLowerCase();
      if (
        texto &&
        ![p.actividadtipo?.nombre, certNombre, p.moneda?.nombre]
          .join(" ")
          .toLowerCase()
          .includes(texto)
      )
        return false;
      if (
        bulkFiltroActividad.length &&
        !bulkFiltroActividad.includes(p.actividadtipo?.nombre)
      )
        return false;
      if (
        bulkFiltroCertificado.length &&
        !bulkFiltroCertificado.includes(certNombre)
      )
        return false;
      return true;
    });

  // Datos del bulk: agrupados por tipo de certificado (expandible).
  // Se recalcula por render para que las filas reflejen el precio nuevo recalculado.
  const bulkGrupos = (() => {
    const mapa = new Map();
    bulkVisibleRows.forEach((p) => {
      if (!mapa.has(p.certificadotipoid)) {
        mapa.set(p.certificadotipoid, {
          id: p.certificadotipoid,
          certNombre: certificadoNombre(p),
          actividades: []
        });
      }
      mapa
        .get(p.certificadotipoid)
        .actividades.push({ ...p, _nuevo: nuevosPrecios[p.id] });
    });
    return [...mapa.values()].sort((a, b) =>
      a.certNombre.localeCompare(b.certNombre)
    );
  })();

  const bulkTotal = bulkVisibleRows.length;
  const visibleIds = new Set(bulkVisibleRows.map((r) => r.id));
  const seleccionVisible = seleccion.filter((s) => visibleIds.has(s.id));

  // Actividades visibles + seleccionadas cuyo precio nuevo difiere del actual (las que cambian)
  const cambiosEfectivos = seleccionVisible.filter(
    (s) =>
      nuevosPrecios[s.id] != null &&
      !isNaN(nuevosPrecios[s.id]) &&
      nuevosPrecios[s.id] !== s.precio
  );

  // Tabla interna (actividades) de cada tipo de certificado en el bulk
  const bulkExpansionTemplate = (grupo) => (
    <div css={{ padding: "0.5rem 1rem" }}>
      <TablaFiltrable conFiltros={false}
        value={grupo.actividades}
        dataKey="id"
        selection={seleccion.filter((s) =>
          grupo.actividades.some((a) => a.id === s.id)
        )}
        onSelectionChange={(e) => mergeGroupSelection(grupo, e.value)}
        size="small"
        rowClassName={(fila) =>
          seleccion.some((s) => s.id === fila.id) ? "" : "fila-no-sel"
        }
        css={{ "& .fila-no-sel": { backgroundColor: "#f9f9f9", opacity: 0.6 } }}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        <Column header="Actividad" body={(f) => f.actividadtipo?.nombre} />
        <Column
          header="Precio actual"
          body={bulkPrecioActualBody}
          style={{ width: "9rem" }}
        />
        <Column
          header="Precio nuevo"
          body={bulkPrecioNuevoBody}
          style={{ width: "11rem" }}
        />
      </TablaFiltrable>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {/* Sidebar de edición individual (título liviano vía Formulario) */}
      <Sidebar
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Formulario
            key={formEditKey}
            config={getEditFormConfig()}
            loader={editLoader}
          />
        </div>
      </Sidebar>

      {/* Sidebar de modificación masiva (ancho, agrupado por tipo de certificado) */}
      <Sidebar
        visible={bulkVisible}
        onHide={() => setBulkVisible(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-6"
      >
        <div css={{ marginBottom: "70px" }}>
          {/* Título liviano (mismo criterio que el Formulario) */}
          <div css={{ fontSize: "1.5em", marginBottom: "0.5em" }}>
            Modificación masiva de precios
          </div>
          <p css={{ color: "var(--grisTextos)" }}>
            Ingrese el incremento en porcentaje y presione Recalcular. El precio
            nuevo se calcula sobre el actual y puede editarse a mano. Seleccione
            las filas a actualizar.
          </p>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "0.5em",
              margin: "1em 0",
              flexWrap: "wrap"
            }}
          >
            <label css={{ fontWeight: 600 }}>Incremento</label>
            <InputNumber
              value={porcentaje}
              onChange={(e) => setPorcentaje(e.value)}
              suffix=" %"
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={2}
              inputStyle={{ width: "6rem" }}
            />
            <Boton callback={recalcularPrecios} azul>
              Recalcular
            </Boton>
          </div>
          {/* Filtros del bulk: acotar a actividades/certificados puntuales */}
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "0.5em",
              marginBottom: "0.5em",
              flexWrap: "wrap"
            }}
          >
            <span
              className="p-input-icon-left"
              css={{
                "& .pi-search": { left: "0.75rem" },
                width: "14em",
                "@media (max-width: 768px)": { width: "100%" }
              }}
            >
              <i className="pi pi-search" />
              <InputText
                value={bulkBusqueda}
                onChange={(e) => setBulkBusqueda(e.target.value)}
                placeholder="Buscar..."
                css={{ width: "100%", paddingLeft: "2.5rem" }}
              />
            </span>
            {actividadOptions.length > 1 && (
              <MultiSelect
                value={bulkFiltroActividad}
                options={actividadOptions}
                onChange={(e) => setBulkFiltroActividad(e.value)}
                placeholder="Actividad"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} actividades"
                showClear
                css={{ width: "13em" }}
              />
            )}
            {certificadoOptions.length > 1 && (
              <MultiSelect
                value={bulkFiltroCertificado}
                options={certificadoOptions}
                onChange={(e) => setBulkFiltroCertificado(e.value)}
                placeholder="Tipo de certificado"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} certificados"
                filter
                showClear
                css={{ width: "15em" }}
              />
            )}
          </div>
          <small css={{ color: "var(--grisTextos)" }}>
            {bulkTotal} asignaciones · {seleccionVisible.length} seleccionadas ·{" "}
            {cambiosEfectivos.length} a modificar
          </small>
          <TablaFiltrable conFiltros={false}
            value={bulkGrupos}
            dataKey="id"
            expandedRows={bulkExpanded}
            onRowToggle={(e) => setBulkExpanded(e.data)}
            rowExpansionTemplate={bulkExpansionTemplate}
            size="small"
            emptyMessage="No hay precios cargados para modificar"
            css={{ marginTop: "0.5em" }}
          >
            <Column expander style={{ width: "3rem" }} />
            <Column header="Tipo de certificado" field="certNombre" />
            <Column
              header="Seleccionadas"
              body={(g) =>
                `${
                  g.actividades.filter((a) =>
                    seleccion.some((s) => s.id === a.id)
                  ).length
                }/${g.actividades.length}`
              }
              align="center"
              alignHeader="center"
              style={{ width: "9em" }}
            />
          </TablaFiltrable>
          <div
            css={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "10px",
              backgroundColor: "white"
            }}
          >
            <Boton
              ancho
              callback={aplicarBulk}
              loader={bulkLoader}
              disabled={cambiosEfectivos.length === 0}
              confirmacion={`¿Está seguro de aplicar los cambios a ${cambiosEfectivos.length} ${cambiosEfectivos.length === 1 ? "actividad" : "actividades"}?`}
            >
              Aplicar cambios
            </Boton>
          </div>
        </div>
      </Sidebar>

      {/* Sidebar de ajuste masivo de propuestas comerciales */}
      <Sidebar
        visible={ajusteVisible}
        onHide={() => setAjusteVisible(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-6"
      >
        {ajusteVisible && (
          <AjustePropuestas onClose={() => setAjusteVisible(false)} />
        )}
      </Sidebar>

      <Titulo
        botones={
          <div css={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Boton azul callback={abrirBulk}>
              Modificación masiva de precios
            </Boton>
            {puedeAjustarPropuestas && (
              <Boton azul callback={() => setAjusteVisible(true)}>
                Ajustar propuestas
              </Boton>
            )}
          </div>
        }
      >
        Lista de precios
      </Titulo>

      {loading && <Skeleton width="100%" height="20rem" borderRadius="6px" />}

      {!loading && precios && (
        <Card css={{ marginTop: "1em" }}>
          {/* Barra de filtros estilo actividades abiertas */}
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "0.5em",
              marginBottom: "0.5em",
              flexWrap: "wrap"
            }}
          >
            <span
              className="p-input-icon-left"
              css={{
                "& .pi-search": { left: "0.75rem" },
                width: "16em",
                "@media (max-width: 768px)": { width: "100%" }
              }}
            >
              <i className="pi pi-search" />
              <InputText
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar..."
                css={{ width: "100%", paddingLeft: "2.5rem" }}
              />
            </span>
            {certificadoOptions.length > 1 && (
              <MultiSelect
                value={filtroCertificado}
                options={certificadoOptions}
                onChange={(e) => setFiltroCertificado(e.value)}
                placeholder="Tipo de certificado"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} certificados"
                filter
                showClear
                css={{ width: "15em" }}
              />
            )}
            {actividadOptions.length > 1 && (
              <MultiSelect
                value={filtroActividad}
                options={actividadOptions}
                onChange={(e) => setFiltroActividad(e.value)}
                placeholder="Actividad"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} actividades"
                showClear
                css={{ width: "13em" }}
              />
            )}
          </div>
          <TablaFiltrable conFiltros={false}
            value={grupos}
            dataKey="id"
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={expansionTemplate}
            stripedRows
            paginator={grupos.length > 20}
            rows={20}
            emptyMessage="No hay precios cargados"
          >
            <Column expander style={{ width: "3rem" }} />
            <Column header="Tipo de certificado" field="certNombre" />
            <Column
              header="Actividades"
              body={(g) => g.actividades.length}
              align="center"
              alignHeader="center"
              style={{ width: "10em" }}
            />
          </TablaFiltrable>
        </Card>
      )}
    </>
  );
}
